import { useGetAnalyticsFilterForJobsQuery } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ListFilter, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";

interface ExportFilterProps {
  selectedJobId: string | null;
  selectedFilters: Record<string, string[]>;
  onFiltersChange: (
    updater: (prev: Record<string, string[]>) => Record<string, string[]>
  ) => void;
}

const ExportFilter = ({
  selectedJobId,
  onFiltersChange,
  selectedFilters,
}: ExportFilterProps) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [tempFilters, setTempFilters] =
    useState<Record<string, string[]>>(selectedFilters);
  const { sendEvent } = useAnalyticsManager();
  const handleFilterSelect = (category: string, value: string) => {
    setTempFilters((prev) => {
      const currentValues = prev[category] || [];
      const isSelected = currentValues.includes(value);
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      sendEvent(
        isSelected
          ? "export_filter_option_deselected"
          : "export_filter_option_selected",
        {
          filter_option: `${category}:${value}`,
        }
      );

      if (newValues.length === 0) {
        const newFilters = { ...prev };
        delete newFilters[category];
        return newFilters;
      }

      return {
        ...prev,
        [category]: newValues,
      };
    });
  };
  const { data: filters } = useGetAnalyticsFilterForJobsQuery(
    selectedJobId ?? "",
    {
      enabled: !!selectedJobId,
    }
  );
  const handleDropdownOpen = () => {
    setTempFilters(selectedFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(() => tempFilters);
    sendEvent("export_filter_option_applied", {
      filter_option: Object.entries(tempFilters)
        .map(([category, values]) => `${category}:[${values.join(",")}]`)
        .join(";"),
    });
  };
  useEffect(() => {
    setTempFilters(selectedFilters);
  }, [selectedFilters]);
  return (
    <div className="flex flex-col justify-between gap-2">
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) {
            handleDropdownOpen();
            sendEvent("export_filters_clicked", {});
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={!selectedJobId}
            className="flex items-center justify-center gap-2 rounded-none border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ListFilter size={16} className="text-brand-muted" />
            <span>Filter</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[400px]" align="start">
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              {/* Categories list on the left */}
              <div className="flex w-[150px] flex-col">
                {filters &&
                  Object.entries(filters).map(([category]) => (
                    <button
                      key={category}
                      className={cn(
                        "flex items-center justify-between border-b border-shades-neutral-100 bg-shades-neutral-50 px-3 py-2 text-left",
                        activeCategory === category ? "bg-white" : ""
                      )}
                      onClick={() => {
                        setActiveCategory(category);
                        sendEvent("export_filter_category_selected", {
                          category,
                        });
                      }}
                    >
                      <span>{category}</span>
                      <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs">
                        {selectedFilters[category]?.length || 0}
                      </span>
                    </button>
                  ))}
              </div>

              {/* Values list on the right */}
              <div className="flex-1">
                {activeCategory && filters && (
                  <div className="h-[300px] overflow-y-auto pr-2">
                    <div className="flex flex-col gap-2">
                      {filters[activeCategory].map((value) => (
                        <button
                          key={value}
                          className="flex items-center gap-2 rounded-md px-3 py-2 text-left hover:bg-gray-50"
                          onClick={() =>
                            handleFilterSelect(activeCategory, value)
                          }
                        >
                          <div
                            className={cn(
                              "flex h-5 w-5 items-center justify-center border transition-colors",
                              tempFilters[activeCategory]?.includes(value)
                                ? "border-brand-accent bg-brand-accent text-white"
                                : "border-brand-border text-transparent"
                            )}
                          >
                            <Check size={14} strokeWidth={3} />
                          </div>
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between border-t py-4">
              <Button
                variant="ghost"
                onClick={() => {
                  setTempFilters({});
                  setActiveCategory(null);
                  sendEvent("export_filter_option_cleared", {
                    filter_option: "all",
                  });
                }}
                className="text-gray-600"
              >
                Clear
              </Button>
              <Button
                variant="outline"
                onClick={handleApplyFilters}
                className="border-brand-border text-brand-text "
              >
                Apply
              </Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ExportFilter;
